import { useMemo } from 'react';
import { useDataCoordinator } from '@/hooks/useDataCoordinator';

interface PlayerStats {
  totalPlayers: number;
  newPlayersThisWeek: number;
  percentIncreaseFromLastMonth: number;
  timeSaved: number; // in hours
}

/**
 * Hook to calculate player statistics
 * Returns statistics about players in the database
 * Uses data from the coordinator to avoid duplicate API calls
 */
export function usePlayerStats(): PlayerStats & { isLoading: boolean } {
  // Get players data from the coordinator instead of making separate API call
  const { players, isInitialLoading } = useDataCoordinator();
  
  // Memoize the players length to avoid unnecessary recalculations
  const playersLength = useMemo(() => players.length, [players.length]);
  
  // Calculate stats when players data changes, but memoize the calculation
  const stats = useMemo(() => {
    if (!players || players.length === 0) {
      // Reset stats if no players
      return {
        totalPlayers: 0,
        newPlayersThisWeek: 0,
        percentIncreaseFromLastMonth: 0,
        timeSaved: 0,
      };
    }
    
    const now = new Date();
    
    // Calculate total players
    const totalPlayers = players.length;
    
    // Calculate new players this week
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    const newPlayersThisWeek = players.filter(player => {
      const playerAddedDate = new Date(player.date_added);
      return playerAddedDate >= oneWeekAgo;
    }).length;
    
    // Calculate percent increase from last month
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    
    const twoMonthsAgo = new Date(now);
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    
    const playersLastMonth = players.filter(player => {
      const playerAddedDate = new Date(player.date_added);
      return playerAddedDate >= oneMonthAgo && playerAddedDate <= now;
    }).length;
    
    const playersTwoMonthsAgo = players.filter(player => {
      const playerAddedDate = new Date(player.date_added);
      return playerAddedDate >= twoMonthsAgo && playerAddedDate < oneMonthAgo;
    }).length;
    
    let percentIncreaseFromLastMonth = 0;
    if (playersTwoMonthsAgo > 0) {
      percentIncreaseFromLastMonth = ((playersLastMonth - playersTwoMonthsAgo) / playersTwoMonthsAgo) * 100;
    } else if (playersLastMonth > 0) {
      percentIncreaseFromLastMonth = 100; // If there were no players two months ago but there are now, that's a 100% increase
    }
    
    // Round to nearest integer
    percentIncreaseFromLastMonth = Math.round(percentIncreaseFromLastMonth);
    
    // Calculate time saved (3 minutes per player as requested)
    const timeSavedMinutes = players.length * 3;
    const timeSaved = timeSavedMinutes / 60; // Convert to hours
    
    return {
      totalPlayers,
      newPlayersThisWeek,
      percentIncreaseFromLastMonth,
      timeSaved,
    };
  }, [playersLength, players]); // Only recalculate when players array actually changes
  
  return {
    ...stats,
    isLoading: isInitialLoading,
  };
}